import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { toast } from "react-toastify";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { queryClient } from "../../queries/client";
import { useCreateLocation, useUpdateLocation } from "../../queries/admin";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { v4 as uuid } from "uuid";
import LocationImage from "./LocationImage";

export interface LocationFormValues {
  locationId: string;
  name: string;
  address: string;
  imageKey: string;
}

const validationSchema = Yup.object().shape({
  name: Yup.string().trim().required("Location name is required"),
  address: Yup.string().trim(),
  imageKey: Yup.string(),
});

export default function AddLocationModal({
  isOpen,
  onClose,
  editingLocation,
}: {
  isOpen: boolean;
  onClose: () => void;
  editingLocation?: any;
}) {
  const { mutate: createLocation, isPending: isCreating } = useCreateLocation();
  const { mutate: updateLocation, isPending: isUpdating } = useUpdateLocation();

  const isPending = isCreating || isUpdating;

  const isEditMode = !!editingLocation;

  const initialValues: LocationFormValues = {
    locationId: editingLocation?.locationId || "",
    name: editingLocation?.name || "",
    address: editingLocation?.address || "",
    imageKey: editingLocation?.imageKey || "",
  };
  const handleSubmit = (
    values: LocationFormValues,
    { setSubmitting, resetForm }: any,
  ) => {
    if (isEditMode) {
      // Update existing location
      updateLocation(
        { ...values, id: editingLocation.id },
        {
          onSuccess: () => {
            toast("Location updated successfully", {
              type: "success",
              autoClose: 2000,
            });
            queryClient.invalidateQueries({ queryKey: ["get-all-locations"] });
            resetForm();
            onClose();
          },
          onError: (error: any) => {
            toast(
              error?.response?.data?.errorMessage ||
                "Failed to update location",
              {
                type: "error",
                autoClose: 2000,
              },
            );
          },
          onSettled: () => {
            setSubmitting(false);
          },
        },
      );
    } else {
      // Create new location
      if (!values.locationId) {
        values.locationId = uuid();
      }
      createLocation(values, {
        onSuccess: () => {
          toast("Location added successfully", {
            type: "success",
            autoClose: 2000,
          });
          queryClient.invalidateQueries({ queryKey: ["get-all-locations"] });
          resetForm();
          onClose();
        },
        onError: (error: any) => {
          toast(
            error?.response?.data?.errorMessage || "Failed to add location",
            {
              type: "error",
              autoClose: 2000,
            },
          );
        },
        onSettled: () => {
          setSubmitting(false);
        },
      });
    }
  };

  return (
    <Transition show={isOpen}>
      <Dialog className="relative z-50" onClose={onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="space-y-2">
                  <div className="border-b border-gray-900/10 dark:border-gray-700 pb-2 flex flex-row items-center justify-between">
                    <h2 className="text-base font-semibold leading-7 text-gray-900 dark:text-white">
                      {isEditMode ? "Edit Location" : "Add Location"}
                    </h2>
                    <XMarkIcon
                      className="w-6 cursor-pointer h-6 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      onClick={onClose}
                    />
                  </div>

                  <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                  >
                    {({ isSubmitting, values, setFieldValue }) => {
                      return (
                        <Form>
                          <div className="border-b border-gray-900/10 dark:border-gray-700">
                            <div className="mt-2 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                              <div className="sm:col-span-6">
                                <label
                                  htmlFor="name"
                                  className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                                >
                                  Location Name *
                                </label>
                                <div className="mt-2">
                                  <Field
                                    id="name"
                                    name="name"
                                    type="text"
                                    autoComplete="off"
                                    placeholder="Enter location name"
                                    className="block w-full px-3 rounded-md border-0 py-2 text-gray-900 dark:text-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                  />
                                  <ErrorMessage
                                    name="name"
                                    component="p"
                                    className="text-red-500 text-sm mt-1"
                                  />
                                </div>
                              </div>

                              <div className="col-span-full">
                                <label
                                  htmlFor="address"
                                  className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                                >
                                  Address
                                </label>
                                <div className="mt-2">
                                  <Field
                                    as="textarea"
                                    id="address"
                                    name="address"
                                    placeholder="Enter location address"
                                    rows={3}
                                    className="block w-full px-3 rounded-md border-0 py-2 text-gray-900 dark:text-white dark:bg-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                  />
                                  <ErrorMessage
                                    name="address"
                                    component="p"
                                    className="text-red-500 text-sm mt-1"
                                  />
                                </div>
                              </div>

                              <div className="col-span-full">
                                <label
                                  htmlFor="image"
                                  className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100"
                                >
                                  Location Map
                                </label>
                                <div className="mt-2">
                                  <LocationImage
                                    imageKey={values.imageKey}
                                    setFieldValue={setFieldValue}
                                  />
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Supported formats: PNG, JPG, JPEG.
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Max. size per file: 50MB
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="mt-6 flex items-center justify-end gap-x-6">
                            <button
                              type="button"
                              onClick={onClose}
                              disabled={isSubmitting || isPending}
                              className="text-sm cursor-pointer font-semibold leading-6 text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={isSubmitting || isPending}
                              className="rounded-md cursor-pointer bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSubmitting || isPending
                                ? isEditMode
                                  ? "Updating..."
                                  : "Adding..."
                                : isEditMode
                                  ? "Update Location"
                                  : "Add Location"}
                            </button>
                          </div>
                        </Form>
                      );
                    }}
                  </Formik>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
